/**
 * Which gateway and merchant account took the money, for the Payments dashboard.
 *
 * Every other figure on this page is a sum of charges that each went to some
 * processor, and the page never said which — so "approvals dropped" and "we
 * moved MIDs" looked identical. This names the routing behind the totals.
 *
 * ONE MID PER TENANT IS THE POINT, not a reason to leave it out. A second
 * merchant account appearing — a backup MID, a migration half-done — shows up
 * nowhere else on the page, and is the first thing to rule out when an approval
 * rate moves.
 *
 * AMOUNTS ARRIVE IN CENTS, and the page's formatCurrency takes DOLLARS. Same
 * trap as DeclinesSection: getting it backwards renders every figure at 1% of
 * its value, which looks plausible.
 */

import { Wallet, ExternalLink } from 'lucide-react'

import { Card, CardHeader, CardTitle, CardContent } from './ui'

const money = (cents, format) => format((Number(cents) || 0) / 100)
const rate = value => (typeof value === 'number' ? `${value.toFixed(1)}%` : '—')

/**
 * The processor's name as a person says it.
 *
 * Hyperswitch's connector ids are lowercase and run together, and two of the
 * four live ones ("authorizedotnet", "cybersource") are unreadable that way.
 * An unknown connector is shown as-is rather than mangled — a new processor
 * should appear by its own name, not as a gap.
 */
const GATEWAY_LABELS = {
  stripe: 'Stripe',
  cybersource: 'Cybersource',
  authorizedotnet: 'Authorize.net',
  nmi: 'NMI',
}

function gatewayName(gateway) {
  return gateway.label || GATEWAY_LABELS[gateway.connector] || gateway.connector
}

export default function GatewaySection({ data, loading, error, formatCurrency, formatNumber }) {
  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">Loading routing…</CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">{error}</CardContent>
      </Card>
    )
  }

  const gateways = data?.gateways || []

  if (gateways.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No payment was attempted in this window, so nothing was routed.
        </CardContent>
      </Card>
    )
  }

  const totals = data.totals || {}

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Wallet className="h-5 w-5" />
          Gateway routing
        </CardTitle>
        <a
          href="https://live.hyperswitch.io/dashboard/connectors"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          <ExternalLink className="h-3.5 w-3.5" /> Connectors in Hyperswitch
        </a>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Gateway</th>
                <th className="text-left p-3 font-medium">MID</th>
                <th className="text-right p-3 font-medium">Attempts</th>
                <th className="text-right p-3 font-medium">Approved</th>
                <th className="text-right p-3 font-medium">Declined</th>
                <th className="text-right p-3 font-medium">Approval rate</th>
                <th className="text-right p-3 font-medium">Collected</th>
              </tr>
            </thead>
            <tbody>
              {gateways.map(gateway => (
                <tr key={`${gateway.connector}-${gateway.merchantConnectorId}`} className="border-t">
                  <td className="p-3 font-medium">{gatewayName(gateway)}</td>
                  <td className="p-3 font-mono text-xs text-muted-foreground">
                    {gateway.merchantConnectorId || '—'}
                  </td>
                  <td className="p-3 text-right">{formatNumber(gateway.attempts)}</td>
                  <td className="p-3 text-right">{formatNumber(gateway.succeeded)}</td>
                  <td className="p-3 text-right">{formatNumber(gateway.failed)}</td>
                  <td className="p-3 text-right font-medium">{rate(gateway.approvalRate)}</td>
                  <td className="p-3 text-right font-mono">{money(gateway.volume, formatCurrency)}</td>
                </tr>
              ))}
              {/* Only when there is more than one route: a totals line under a
                  single row repeats it. */}
              {gateways.length > 1 && (
                <tr className="border-t bg-muted/30 font-medium">
                  <td className="p-3" colSpan={2}>All gateways</td>
                  <td className="p-3 text-right">{formatNumber(totals.attempts)}</td>
                  <td className="p-3 text-right">{formatNumber(totals.succeeded)}</td>
                  <td className="p-3 text-right">{formatNumber(totals.failed)}</td>
                  <td className="p-3 text-right">{rate(totals.approvalRate)}</td>
                  <td className="p-3 text-right font-mono">{money(totals.volume, formatCurrency)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Said rather than implied: the attempt counts here are smaller than
            the payment counts elsewhere on the page, and this is why. */}
        {data.neverAttempted > 0 && (
          <p className="mt-3 text-xs text-muted-foreground">
            {formatNumber(data.neverAttempted)} checkouts loaded without a card ever being entered.
            No gateway was chosen for those, so they are not counted above.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
